import { GracenoteTeam } from "types";
import { useGracenoteTeams } from "./gracenote";

/**
 * Resolves a map of Gracenote teams grouped by sport ID for the given sports data.
 *
 * Combines the caller-provided `sportsData` list with live Gracenote team data
 * fetched via {@link useGracenoteTeams}. Returns `undefined` while the Gracenote
 * data is still loading.
 *
 * @param sportsData - Array of sport objects, each containing a `sport.id` and `sport.label`.
 * @returns A record keyed by sport ID with arrays of matching {@link GracenoteTeam} objects,
 *   or `undefined` if teams are still being fetched.
 *
 * @example
 * const teamsBySport = useTeams(workplace.sports);
 * if (teamsBySport) {
 *   const soccerTeams = teamsBySport['42'];
 * }
 */
export const useTeams = (sportsData: any) => {

    const { data, isLoading } = useGracenoteTeams();

    const transformedArray = sportsData.map((item) => ({
        sportsId: item.sport.id.toString(),
        sportsName: item.sport.label || item.sport.sportsName,
    }));

    if (isLoading) {
        return undefined;
    }

    return transformedArray.reduce(
        (acc, iter) => {
            return {
                ...acc,
                [iter.sportsId]:
                    data?.filter((team) => `${team.sport.id}` == iter.sportsId) || [],
            };
        },
        {} as Record<string, GracenoteTeam[]>
    );
};